import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { isRefetchingWithNewParams } from "./isRefetchingWithNewParams";

export interface TokenPagedResult<TItem, Extra = never> {
  items: TItem[];
  nextToken?: string | null;
  extra?: Extra;
}

interface UseTokenPagedInfiniteQueryOptions<TItem, Extra = never> {
  queryKey: readonly unknown[];
  fetchPage: (nextToken?: string) => Promise<TokenPagedResult<TItem, Extra>>;
  enabled?: boolean;
}

export function useTokenPagedInfiniteQuery<TItem, Extra = never>({
  queryKey,
  fetchPage,
  enabled = true,
}: UseTokenPagedInfiniteQueryOptions<TItem, Extra>) {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextToken ?? undefined,
    enabled,
    placeholderData: keepPreviousData,
  });

  const items = useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  return {
    ...query,
    items,
    extra: query.data?.pages[0]?.extra,
    isRefetchingWithNewParams: isRefetchingWithNewParams(query),
  };
}
